import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, Filter, ArrowUpDown, ArrowDown, ArrowUp, AlertTriangle, CheckCircle2, Clock, FileText, DollarSign, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loan, LoanStage, Scope, STAGE_LABELS, STAGE_COLORS, filterLoansByStageAndScope, formatCurrency, formatDate, getEventDate, LOANS } from '../data/loans';
interface LoanFilesPanelProps {
  open: boolean;
  initialStage: LoanStage | null;
  initialScope: Scope;
  onClose: () => void;
}
type SortKey = 'date' | 'amount' | 'borrower' | 'daysInStage';
type SortDir = 'asc' | 'desc';
const LOAN_TYPES = ['Conventional', 'FHA', 'VA', 'Jumbo'] as const;
export function LoanFilesPanel({
  open,
  initialStage,
  initialScope,
  onClose
}: LoanFilesPanelProps) {
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<LoanStage | 'all'>(initialStage ?? 'all');
  const [scope, setScope] = useState<Scope>(initialScope);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [movingFilter, setMovingFilter] = useState<'all' | 'moving' | 'stuck'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  // Reset filters when reopened with a new stage
  useEffect(() => {
    if (open) {
      setStage(initialStage ?? 'all');
      setScope(initialScope);
      setSearch('');
      setTypeFilter([]);
      setMovingFilter('all');
      setSortKey('date');
      setSortDir('desc');
    }
  }, [open, initialStage, initialScope]);
  // Close on escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);
  const filteredLoans = useMemo(() => {
    let result: Loan[] = LOANS;
    // Stage + scope filter
    if (stage !== 'all') {
      result = filterLoansByStageAndScope(result, stage, scope);
    } else if (scope !== 'all') {
      // For 'all' stage, filter by any event in scope
      result = result.filter(loan => loan.events.some(e => {
        if (scope === 'today') return e.date === '2025-05-14';
        if (scope === 'mtd') return e.date >= '2025-05-01' && e.date <= '2025-05-14';
        return true;
      }));
    }
    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(l => l.borrower.toLowerCase().includes(q) || l.property.toLowerCase().includes(q) || l.id.toLowerCase().includes(q));
    }
    // Type filter
    if (typeFilter.length > 0) {
      result = result.filter(l => typeFilter.includes(l.loanType));
    }
    // Moving filter
    if (movingFilter === 'moving') result = result.filter(l => l.isMoving);
    if (movingFilter === 'stuck') result = result.filter(l => !l.isMoving);
    // Sort
    const sorted = [...result].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'date') {
        const aDate = stage !== 'all' ? getEventDate(a, stage as LoanStage) : a.events[a.events.length - 1].date;
        const bDate = stage !== 'all' ? getEventDate(b, stage as LoanStage) : b.events[b.events.length - 1].date;
        cmp = (aDate ?? '').localeCompare(bDate ?? '');
      } else if (sortKey === 'amount') {
        cmp = a.loanAmount - b.loanAmount;
      } else if (sortKey === 'borrower') {
        cmp = a.borrower.localeCompare(b.borrower);
      } else if (sortKey === 'daysInStage') {
        cmp = a.daysInCurrentStage - b.daysInCurrentStage;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [stage, scope, search, typeFilter, movingFilter, sortKey, sortDir]);
  const totalPoints = useMemo(() => {
    return filteredLoans.reduce((sum, l) => {
      if (stage !== 'all') {
        const e = l.events.find(ev => ev.stage === stage);
        return e ? sum + e.points : sum;
      }
      return sum + l.events.reduce((s, e) => s + e.points, 0);
    }, 0);
  }, [filteredLoans, stage]);
  const totalVolume = filteredLoans.reduce((s, l) => s + l.loanAmount, 0);
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };
  const toggleType = (type: string) => {
    setTypeFilter(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };
  const clearFilters = () => {
    setSearch('');
    setStage('all');
    setTypeFilter([]);
    setMovingFilter('all');
  };
  const headerTitle = stage !== 'all' ? `${STAGE_LABELS[stage as LoanStage]}s` : 'All Loan Files';
  const scopeLabel = scope === 'today' ? 'Today' : scope === 'mtd' ? 'Month to Date' : 'All Time';
  return <AnimatePresence data-id="element-40">
      {open && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/40 z-40" onClick={onClose} data-id="element-41" />

          {/* Panel */}
          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 280
      }} className="fixed top-0 right-0 bottom-0 w-full md:w-[860px] bg-gray-50 z-50 shadow-2xl flex flex-col" role="dialog" aria-label="Loan files drill-down" data-id="element-42">
            {/* Header */}
            <div className="bg-[#0a1e5e] text-white px-6 py-4 flex items-center justify-between shrink-0" data-id="element-43">
              <div data-id="element-44">
                <p className="text-xs font-semibold text-blue-200 uppercase tracking-wider" data-id="element-45">
                  {scopeLabel} · Drill-down
                </p>
                <h2 className="text-2xl font-extrabold tracking-tight" data-id="element-46">
                  {headerTitle}
                </h2>
              </div>
              <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" aria-label="Close panel" data-id="element-47">
                <X className="w-5 h-5" data-id="element-48" />
              </button>
            </div>

            {/* Summary Strip */}
            <div className="bg-white border-b border-gray-200 px-6 py-3 grid grid-cols-3 gap-4 shrink-0" data-id="element-49">
              <div data-id="element-50">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-51">
                  Files
                </p>
                <p className="text-2xl font-extrabold text-[#0a1e5e]" data-id="element-52">
                  {filteredLoans.length}
                </p>
              </div>
              <div data-id="element-53">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-54">
                  Total Volume
                </p>
                <p className="text-2xl font-extrabold text-[#0a1e5e]" data-id="element-55">
                  {formatCurrency(totalVolume)}
                </p>
              </div>
              <div data-id="element-56">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-57">
                  Points
                </p>
                <p className="text-2xl font-extrabold text-green-600" data-id="element-58">
                  {totalPoints.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0" data-id="element-59">
              <div className="flex flex-col gap-3" data-id="element-60">
                {/* Search + Scope */}
                <div className="flex flex-col md:flex-row gap-3" data-id="element-61">
                  <div className="relative flex-1" data-id="element-62">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-63" />
                    <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search borrower, property, or loan ID..." className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-64" />
                  </div>
                  <div className="flex bg-gray-100 rounded-lg p-1" data-id="element-65">
                    {(['today', 'mtd', 'all'] as Scope[]).map(s => <button key={s} onClick={() => setScope(s)} className={`px-3 py-1 text-xs font-bold uppercase rounded-md transition-colors ${scope === s ? 'bg-white text-[#0a1e5e] shadow-sm' : 'text-gray-600 hover:text-[#0a1e5e]'}`} data-id="element-66">
                        {s === 'today' ? 'Today' : s === 'mtd' ? 'MTD' : 'All'}
                      </button>)}
                  </div>
                </div>

                {/* Stage Pills */}
                <div className="flex flex-wrap gap-2 items-center" data-id="element-67">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mr-1" data-id="element-68">
                    Stage:
                  </span>
                  <button onClick={() => setStage('all')} className={`px-3 py-1 text-xs font-bold rounded-full border transition-colors ${stage === 'all' ? 'bg-[#0a1e5e] text-white border-[#0a1e5e]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`} data-id="element-69">
                    All
                  </button>
                  {(Object.keys(STAGE_LABELS) as LoanStage[]).map(s => {
                const c = STAGE_COLORS[s];
                const active = stage === s;
                return <button key={s} onClick={() => setStage(s)} className={`px-3 py-1 text-xs font-bold rounded-full border transition-colors ${active ? `${c.bg} ${c.text} ${c.border}` : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`} data-id="element-70">
                        {STAGE_LABELS[s]}
                      </button>;
              })}
                </div>

                {/* Type + Moving */}
                <div className="flex flex-wrap gap-2 items-center" data-id="element-71">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mr-1" data-id="element-72">
                    Type:
                  </span>
                  {LOAN_TYPES.map(t => {
                const active = typeFilter.includes(t);
                return <button key={t} onClick={() => toggleType(t)} className={`px-2.5 py-1 text-xs font-semibold rounded border transition-colors ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`} data-id="element-73">
                        {t}
                      </button>;
              })}
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider ml-3 mr-1" data-id="element-74">
                    Status:
                  </span>
                  {(['all', 'moving', 'stuck'] as const).map(m => <button key={m} onClick={() => setMovingFilter(m)} className={`px-2.5 py-1 text-xs font-semibold rounded border transition-colors ${movingFilter === m ? m === 'stuck' ? 'bg-red-100 text-red-700 border-red-200' : m === 'moving' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-[#0a1e5e] text-white border-[#0a1e5e]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`} data-id="element-75">
                      {m === 'all' ? 'All' : m === 'moving' ? 'Moving' : 'Not Moving'}
                    </button>)}
                  {(search || stage !== 'all' || typeFilter.length > 0 || movingFilter !== 'all') && <button onClick={clearFilters} className="ml-auto text-xs font-bold text-blue-600 hover:text-blue-800 underline" data-id="element-76">
                      Clear filters
                    </button>}
                </div>
              </div>
            </div>

            {/* Sort Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-2 shrink-0 hidden md:grid grid-cols-12 gap-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-77">
              <button onClick={() => toggleSort('borrower')} className="col-span-4 text-left flex items-center gap-1 hover:text-[#0a1e5e]" data-id="element-78">
                Borrower / Property
                <SortIcon active={sortKey === 'borrower'} dir={sortDir} data-id="element-79" />
              </button>
              <div className="col-span-2" data-id="element-80">Stage</div>
              <button onClick={() => toggleSort('amount')} className="col-span-2 text-right flex items-center gap-1 justify-end hover:text-[#0a1e5e]" data-id="element-81">
                Amount
                <SortIcon active={sortKey === 'amount'} dir={sortDir} data-id="element-82" />
              </button>
              <button onClick={() => toggleSort('date')} className="col-span-2 text-left flex items-center gap-1 hover:text-[#0a1e5e]" data-id="element-83">
                Stage Date
                <SortIcon active={sortKey === 'date'} dir={sortDir} data-id="element-84" />
              </button>
              <button onClick={() => toggleSort('daysInStage')} className="col-span-2 text-right flex items-center gap-1 justify-end hover:text-[#0a1e5e]" data-id="element-85">
                Days
                <SortIcon active={sortKey === 'daysInStage'} dir={sortDir} data-id="element-86" />
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto px-4 md:px-6 py-3" data-id="element-87">
              {filteredLoans.length === 0 ? <div className="text-center py-16" data-id="element-88">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-89" />
                  <p className="text-gray-500 font-semibold" data-id="element-90">
                    No loan files match these filters
                  </p>
                  <button onClick={clearFilters} className="mt-3 text-sm text-blue-600 font-bold hover:underline" data-id="element-91">
                    Clear filters
                  </button>
                </div> : <ul className="space-y-2" data-id="element-92">
                  {filteredLoans.map(loan => <LoanRow key={loan.id} loan={loan} contextStage={stage !== 'all' ? stage as LoanStage : undefined} data-id="element-93" />)}
                </ul>}
            </div>
          </motion.aside>
        </>}
    </AnimatePresence>;
}
function SortIcon({
  active,
  dir
}: {
  active: boolean;
  dir: SortDir;
}) {
  if (!active) return <ArrowUpDown className="w-3 h-3 opacity-40" data-id="element-94" />;
  return dir === 'asc' ? <ArrowUp className="w-3 h-3" data-id="element-95" /> : <ArrowDown className="w-3 h-3" data-id="element-96" />;
}
function LoanRow({
  loan,
  contextStage
}: {
  loan: Loan;
  contextStage?: LoanStage;
}) {
  const [expanded, setExpanded] = useState(false);
  const c = STAGE_COLORS[loan.currentStage];
  const displayDate = contextStage ? getEventDate(loan, contextStage) : loan.events[loan.events.length - 1].date;
  return <li className={`bg-white border ${expanded ? 'border-blue-300 shadow-md' : 'border-gray-200'} rounded-lg transition-all`} data-id="element-97">
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left p-3 md:grid md:grid-cols-12 md:gap-3 md:items-center flex flex-col gap-2 hover:bg-gray-50/50 rounded-lg" data-id="element-98">
        {/* Borrower */}
        <div className="col-span-4 min-w-0" data-id="element-99">
          <p className="font-bold text-[#0a1e5e] text-sm truncate" data-id="element-100">
            {loan.borrower}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-101">{loan.property}</p>
          <p className="text-[10px] text-gray-400 font-mono" data-id="element-102">
            {loan.id} · {loan.loanType}
          </p>
        </div>

        {/* Stage */}
        <div className="col-span-2" data-id="element-103">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold ${c.bg} ${c.text}`} data-id="element-104">
            {loan.isMoving ? <CheckCircle2 className="w-3 h-3" data-id="element-105" /> : <AlertTriangle className="w-3 h-3" data-id="element-106" />}
            {STAGE_LABELS[loan.currentStage]}
          </span>
        </div>

        {/* Amount */}
        <div className="col-span-2 md:text-right" data-id="element-107">
          <p className="text-sm font-bold text-[#0a1e5e]" data-id="element-108">
            {formatCurrency(loan.loanAmount)}
          </p>
        </div>

        {/* Date */}
        <div className="col-span-2" data-id="element-109">
          <p className="text-xs text-gray-600" data-id="element-110">
            {displayDate ? formatDate(displayDate) : '—'}
          </p>
        </div>

        {/* Days */}
        <div className="col-span-2 md:text-right flex md:block items-center justify-between" data-id="element-111">
          <p className={`text-sm font-bold ${loan.daysInCurrentStage > 5 && !loan.isMoving ? 'text-red-600' : 'text-gray-700'}`} data-id="element-112">
            {loan.daysInCurrentStage}d
            <span className="text-[10px] text-gray-400 font-medium ml-1" data-id="element-113">
              in stage
            </span>
          </p>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} data-id="element-114" />
        </div>
      </button>

      {expanded && <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: 'auto',
      opacity: 1
    }} className="border-t border-gray-100 px-4 py-3 bg-gray-50/50 overflow-hidden" data-id="element-115">
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-116">
            Stage Timeline
          </p>
          <ol className="relative space-y-2 pl-4 border-l-2 border-gray-200" data-id="element-117">
            {loan.events.map(ev => {
          const ec = STAGE_COLORS[ev.stage];
          return <li key={ev.stage} className="relative" data-id="element-118">
                  <span className={`absolute -left-[19px] top-1.5 w-3 h-3 rounded-full ${ec.bg} ${ec.text} border-2 border-white`} data-id="element-119"></span>
                  <div className="flex items-center justify-between" data-id="element-120">
                    <div data-id="element-121">
                      <p className="text-xs font-bold text-[#0a1e5e]" data-id="element-122">
                        {STAGE_LABELS[ev.stage]}
                      </p>
                      <p className="text-[10px] text-gray-500" data-id="element-123">
                        {formatDate(ev.date)}
                      </p>
                    </div>
                    <span className={`text-xs font-bold ${ec.text}`} data-id="element-124">
                      +{ev.points} pts
                    </span>
                  </div>
                </li>;
        })}
          </ol>
          {loan.speedBonus && <div className="mt-3 bg-orange-50 border border-orange-200 rounded-lg p-2 flex items-center gap-2" data-id="element-125">
              <Clock className="w-4 h-4 text-orange-500" data-id="element-126" />
              <p className="text-xs font-semibold text-orange-700" data-id="element-127">
                Speed Bonus: +{loan.speedBonus} pts (fast close)
              </p>
            </div>}
          {!loan.isMoving && <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2 flex items-center gap-2" data-id="element-128">
              <AlertTriangle className="w-4 h-4 text-red-500" data-id="element-129" />
              <p className="text-xs font-semibold text-red-700" data-id="element-130">
                File not moving — losing 50 pts/day until status changes
              </p>
            </div>}
        </motion.div>}
    </li>;
}